import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ElementRef,
    OnDestroy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Import from services
import {
    GanttItem,
    GanttDataMapper,
    GanttConfig,
    GanttTemplateContext,
    MonthHeader,
    GanttChartService,
    GanttState,
    TaskPosition,
} from './services';

interface VisibleRow<T> {
    item: GanttItem<T>;
    depth: number;
    taskPosition: TaskPosition;
    isExpanded: boolean;
    hasChildren: boolean;
}

@Component({
    selector: 'app-gantt-chart',
    templateUrl: './gantt-chart.component.html',
    styleUrls: ['./gantt-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class GanttChartComponent<T = unknown>
    implements OnInit, OnChanges, OnDestroy
{
    @ViewChild('ganttContainer', { static: false })
    public ganttContainer: ElementRef<HTMLElement>;

    // Generic inputs
    @Input() public rawData: T[] = [];
    @Input() public dataMapper: GanttDataMapper<T>;
    @Input() public config: GanttConfig = {};
    @Input() public startDate?: Date;
    @Input() public endDate?: Date;
    @Input() public title?: string;
    @Input() public isLoading = false;
    @Input() public taskTemplate?: TemplateRef<GanttTemplateContext<T>>;
    @Input() public subtaskTemplate?: TemplateRef<GanttTemplateContext<T>>;
    @Input() public adjustDatesBy?: number = 0;
    @Input() public progress?: number = 0;
    @Input() public pdfExportName?: string;

    // For backward compatibility - will be converted using dataMapper
    @Input() public data: GanttItem<T>[] = [];

    @Output() public taskClick = new EventEmitter<GanttItem<T>>();
    @Output() public taskToggle = new EventEmitter<string>();
    @Output() public rawTaskClick = new EventEmitter<T>();

    // Public properties exposed from orchestrator state
    public processedData: GanttItem<T>[] = [];
    public adjustedStartDate: Date | null = null;
    public adjustedEndDate: Date | null = null;
    public displayDates: Date[] = [];
    public displayMonths: MonthHeader[] = [];
    public mergedConfig: GanttConfig = {};
    public visibleRows: VisibleRow<T>[] = [];
    public hasExpandableItems = false;
    public isAllExpanded = false;
    public expandAllLabelKey = 'translate_expand_all';

    // Private properties
    private destroy$ = new Subject<void>();
    private orchestrator: GanttChartService<T>;

    public constructor(
        private readonly cdr: ChangeDetectorRef,
        private readonly orchestratorService: GanttChartService<T>
    ) {
        this.orchestrator = this.orchestratorService;
    }

    public ngOnInit(): void {
        // Subscribe to orchestrator state changes
        this.orchestrator.state$
            .pipe(takeUntil(this.destroy$))
            .subscribe((state: GanttState<T>) => {
                this.updateComponentFromState(state);
                this.cdr.markForCheck();
            });

        // Initialize chart with current inputs
        this.initializeChart();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['rawData'] ||
            changes['dataMapper'] ||
            changes['data'] ||
            changes['startDate'] ||
            changes['endDate'] ||
            changes['config']
        ) {
            this.initializeChart();
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.orchestrator.resetState();
    }

    public toggleTask(taskId: string): void {
        this.orchestrator.toggleTask(taskId);
        this.taskToggle.emit(taskId);
    }

    public toggleExpandAll(): void {
        this.orchestrator.toggleExpandAll();
    }

    public onTaskClick(item: GanttItem<T>): void {
        this.taskClick.emit(item);
        if (item.data) {
            this.rawTaskClick.emit(item.data);
        }
    }

    // TrackBy functions for better performance
    public trackByVisibleRow(_index: number, row: VisibleRow<T>): string {
        return `${row.item.id}-${row.depth}`;
    }

    public trackByMonth(index: number, month: MonthHeader): string {
        return `${month.key}-${index}`;
    }

    // Method to format tooltip with item name and dates
    public formatTooltip(item: GanttItem<T>): string {
        return this.orchestrator.formatTooltip(item);
    }

    public getAdjustedStartDate(): Date {
        return this.adjustedStartDate;
    }

    public getAdjustedEndDate(): Date | null {
        return this.adjustedEndDate;
    }

    public get isToolbarVisible(): boolean {
        return (
            !this.isLoading &&
            this.processedData.length > 0 &&
            (!!this.pdfExportName ||
                (this.mergedConfig.allowExpansion && this.hasExpandableItems))
        );
    }

    public getGanttElementForExport = (): HTMLElement[] =>
        this.ganttContainer?.nativeElement
            ? [this.ganttContainer.nativeElement]
            : [];

    /**
     * Initializes the chart with current input values
     */
    private initializeChart(): void {
        try {
            // Validate inputs before initialization
            const validationResult = this.validateInputs();
            if (!validationResult.isValid) {
                return;
            }

            this.orchestrator.initializeChart(
                this.rawData?.length ? this.rawData : null,
                this.dataMapper,
                this.data?.length ? this.data : null,
                this.config,
                this.startDate,
                this.endDate,
                this.adjustDatesBy
            );
        } catch {
            // Optionally emit an error event or show user-friendly error
        }
    }

    /**
     * Validates component inputs with improved async loading handling
     */
    private validateInputs(): {
        isValid: boolean;
        shouldWarn: boolean;
        message?: string;
    } {
        // Check if we have either rawData with mapper or processed data
        const hasRawData = this.rawData?.length > 0;
        const hasProcessedData = this.data?.length > 0;
        const hasDataMapper = !!this.dataMapper;

        // If no data at all, check if we're in a loading state
        if (!hasRawData && !hasProcessedData) {
            // During loading, this is expected - don't warn
            if (this.isLoading) {
                return { isValid: false, shouldWarn: false };
            }
            // Not loading but no data - could be valid (empty state) or still loading async
            // Don't warn immediately, let the parent component handle empty states
            return { isValid: false, shouldWarn: false };
        }

        // If rawData is provided, dataMapper must be provided
        if (hasRawData && !hasDataMapper) {
            return {
                isValid: false,
                shouldWarn: true,
                message: 'dataMapper is required when rawData is provided',
            };
        }

        return { isValid: true, shouldWarn: false };
    }

    /**
     * Updates component properties from orchestrator state
     */
    private updateComponentFromState(state: GanttState<T>): void {
        this.processedData = state.processedData;
        this.adjustedStartDate = state.adjustedStartDate;
        this.adjustedEndDate = state.adjustedEndDate;
        this.isLoading = state.isLoading;
        this.mergedConfig = state.mergedConfig;

        if (state.timelineData) {
            this.displayDates = state.timelineData.dates;
            this.displayMonths = state.timelineData.monthHeaders;
        } else {
            this.displayDates = [];
            this.displayMonths = [];
        }

        this.visibleRows = this.buildVisibleRows();
        const expandableIds = this.orchestrator.getExpandableTaskIds(
            this.processedData
        );
        this.hasExpandableItems = expandableIds.size > 0;
        this.isAllExpanded = this.orchestrator.areAllExpanded(expandableIds);
        this.expandAllLabelKey = this.isAllExpanded
            ? 'translate_collapse_all'
            : 'translate_expand_all';
    }

    private buildVisibleRows(): VisibleRow<T>[] {
        const rows: VisibleRow<T>[] = [];
        const visit = (items: GanttItem<T>[], depth: number): void => {
            items.forEach(item => {
                const hasChildren = this.orchestrator.hasChildren(item);
                const isExpanded = this.orchestrator.isTaskExpanded(item.id);

                rows.push({
                    item,
                    depth,
                    taskPosition: this.orchestrator.getTaskPosition(item),
                    isExpanded,
                    hasChildren,
                });

                if (
                    this.mergedConfig.allowExpansion &&
                    hasChildren &&
                    isExpanded &&
                    item.children?.length
                ) {
                    visit(item.children, depth + 1);
                }
            });
        };
        visit(this.processedData, 0);
        return rows;
    }
}
